// src/components/PostUpload/PostUpload.tsx
import React, { useState, useRef, useEffect } from "react";
import { useUserContext } from "../../UserContext";
import { generateClient } from "aws-amplify/api";
import { createPost } from "../../graphql/mutations";
import { uploadData, getUrl } from "aws-amplify/storage";
import "./PostUpload.css";
import Avatar from "../UserAvatar/Avatar";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";

interface PostUploadProps {
  onPostSuccess: () => void;
}

const PostUpload: React.FC<PostUploadProps> = ({ onPostSuccess }) => {
  const { user } = useUserContext();
  const [postText, setPostText] = useState("");
  const [mood, setMood] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const client = generateClient();

  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const handlePost = async () => {
    if (!user || loading) return;
    setLoading(true);

    let imageKey: string | null = null;
    if (imageFile) {
      const key = `posts/${Date.now()}-${imageFile.name}`;
      try {
        await uploadData({
          key,
          data: imageFile,
          options: {
            accessLevel: "guest", // or "public" if you're migrating from v5
            contentType: imageFile.type,
          },
        }).result;
        imageKey = key;
      } catch (err) {
        console.error("Error uploading image:", err);
      }
    }

    try {
      await client.graphql({
        query: createPost,
        variables: {
          input: {
            userId: user.id,
            username: user.username,
            avatarUrl: user.avatarUrl,
            content: postText,
            mood,
            imageUrl: imageKey, // Store the S3 key, not the full URL
            createdAt: new Date().toISOString(),
          },
        },
      });
      setPostText("");
      setMood("");
      setImageFile(null);
      setPreviewUrl(null);
      onPostSuccess();
    } catch (error) {
      console.error("Error creating post:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    try {
      const tempKey = `temp-preview-${Date.now()}-${file.name}`;
      await uploadData({
        key: tempKey,
        data: file,
        options: { accessLevel: "guest", contentType: file.type },
      }).result;
      const { url } = await getUrl({
        key: tempKey,
        options: { accessLevel: "guest" },
      });
      setPreviewUrl(url.href);
    } catch (err) {
      console.error("Failed to generate preview:", err);
    }
  };

  return (
    <div className="post-box">
      <div className="post-input-row">
        <Avatar
          avatarUrl={user?.avatarUrl}
          name={user?.username}
          size={40}
          className="avatar-img"
        />
        <textarea
          value={postText}
          onChange={(e) => setPostText(e.target.value)}
          placeholder={`What's on your mind, ${capitalize(
            user?.username || ""
          )}?`}
          className="post-input-box"
        />
      </div>

      <div className="divider" />

      <div className="post-actions-row">
        <input
          className="mood-input"
          value={mood}
          onChange={(e) => setMood(e.target.value)}
          placeholder="What's your mood? Happy? Sad? 🤔"
        />

        <button
          className={`photo-upload-icon ${imageFile ? "photo-selected" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          title="Add photo"
        >
          <AddAPhotoIcon />
        </button>

        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleFileChange}
          hidden
        />

        <button onClick={handlePost} className="post-button" disabled={loading}>
          {loading ? "Posting..." : "Post"}
        </button>
      </div>

      {previewUrl && (
        <img src={previewUrl} alt="Preview" className="preview-image" />
      )}
    </div>
  );
};

export default PostUpload;
